
import Vue, { reactive } from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import {
  AttachmentsAPIControllerUIClient,
  AttachmentType,
  CardAttribute,
  CardAttributeDefinition,
  LinkAttachmentRequest,
  MediaApiControllerUIClient,
} from '../../../../../backend/backendapi';
import Teleport from 'vue2-teleport';
import VueformPlugin from '@vueform/vueform/plugin';
import vueformConfig from '../../../../../../vueform.config';
import { initClient } from '../../../../../../api';
import { FileElement, MultifileElement, SignatureElement } from '@vueform/vueform';

Vue.use(Teleport); // required by vueform, backport of vue3 builtin
Vue.use(VueformPlugin, vueformConfig);

@Component
export default class VueFormAttribute extends Vue {
  @Prop({ required: true })
  readonly definition!: CardAttributeDefinition;
  @Prop({ required: true })
  readonly attribute!: CardAttribute;

  vueFormRerender = false;
  private lastModalDisplayValue = 'none';
  private modalDisplayObserver: MutationObserver | null = null;

  vueFormData: unknown = {};
  ignoreUpdateOnAttribute = false;

  get vueFormSchema(): any {
    const schema = this.definition.metaData ? JSON.parse(this.definition.metaData) : {};
    this.dropEndpoints(schema);
    this.injectEndpoints(schema);
    if (schema.rcConfig) { //remove rcConfig, leads to vue form print ing error because it does not know the element
      delete schema.rcConfig;
    }
    return schema;
  }

  mounted(): void {
    this.addModalOpenListener();
    (this as any).$vueform.i18n.locale = this.$i18n.locale;
  }

  beforeDestroy(): void {
    this.removeModalOpenListener();
  }

  private addModalOpenListener(): void {
    //this is needed so the signature pad works properly in a modal
    let targetNode = this.$el as HTMLElement;
    do {
      targetNode = targetNode.parentElement as HTMLElement;
    } while (targetNode && !targetNode.classList.contains('modal'));

    if (targetNode) {
      this.vueFormRerender = targetNode.style.display === 'block';

      this.modalDisplayObserver = new MutationObserver((mutations: MutationRecord[], observer: MutationObserver) => {
        let styleMutations = mutations.filter(e => e.attributeName === 'style');
        if (styleMutations.length) {
          try {
            if (targetNode.style.display !== 'block') {
              this.vueFormRerender = false;
              return;
            }

            if (this.lastModalDisplayValue !== 'block') {
              this.vueFormRerender = false;
              this.$nextTick(() => {
                this.vueFormRerender = true;
              });
            }
          } finally {
            this.lastModalDisplayValue = targetNode.style.display;
          }
        }
      });
      this.modalDisplayObserver.observe(targetNode, { attributes: true });
    } else {
      this.vueFormRerender = true;
    }
  }

  private dropEndpoints(schema: object) {
    Object.keys(schema).forEach(key => {
      if (['uploadTempEndpoint', 'removeTempEndpoint', 'removeEndpoint'].includes(key)) {
        delete schema[key];
      } else if (typeof schema[key] === 'object') {
        this.dropEndpoints(schema[key]); // recursively drop endpoints
      }
    });
  }

  private injectEndpoints(schema: object) {
    if (['file', 'multifile', 'signature'].includes(schema['type'])) {
      schema['url'] = false;
      schema['previewUrl'] = undefined;
      schema['uploadTempEndpoint'] = async (value: File, el$: FileElement | MultifileElement | SignatureElement) => {
        const mediaApi = initClient(MediaApiControllerUIClient);
        const attachmentApi = initClient(AttachmentsAPIControllerUIClient);

        const media = await mediaApi.upload(undefined, undefined, [{ data: value, fileName: value.name }]);
        await attachmentApi.linkAttachment(LinkAttachmentRequest.fromJS({
          cardId: this.attribute.cardId,
          mediaId: media.fileId,
          type: AttachmentType.VueForm,
        }));
        return media.publicUrl;
      };
      schema['removeEndpoint'] = async (value: string, el$: FileElement | MultifileElement | SignatureElement) => {
        const attachmentApi = initClient(AttachmentsAPIControllerUIClient);

        try {
          const attachment = await attachmentApi.getAttachmentByMediaPublicUrl(value, this.attribute.cardId);
          await attachmentApi.delete(attachment.id, this.attribute.cardId);
        } catch (err) {
          console.error(err);
        }
      };
    } else {
      Object.keys(schema).forEach(key => {
        if (typeof schema[key] === 'object') {
          this.injectEndpoints(schema[key]); // recursively inject endpoints
        }
      });
    }
  }

  private removeModalOpenListener(): void {
    this.modalDisplayObserver?.disconnect();
  }

  // -----------------------------
  // there used to be a computed getter and setter to convert the attribute.value json string to and object and back 
  // but this removes the reactivity from the object, vue then makes the object reactive again. this happens with every change, 
  // i.e. with each key stoke in a text field, this causes a lot of recalculations with are unnecessary and slow down the ui 
  // drastically to the point it becomes laggy or even unusable
  // therefore there is the vueFormData variable now that is kept in sync but it is not serialized and deserialized with every change
  // -----------------------------
  @Watch('attribute.value', { immediate: true })
  onAttributeValueChanged(): void {
    if (this.ignoreUpdateOnAttribute) {
      return;
    }
    const ret = this.attribute.value?.trim().length ? JSON.parse(this.attribute.value) : {};
    this.vueFormData = reactive(ret); //needs to be reactive, otherwise v-model does not work properly
  }

  @Watch('vueFormData')
  onVueFormDataChanged(): void {
    const blobToBase64 = (blob: Blob) => {
      return new Promise((resolve, _) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    };

    const convertBlobs = async (o: any) => {
      for (let key of Object.keys(o)) {
        if (o[key] instanceof Blob) {
          o[key] = await blobToBase64(o[key]);
        }
      }
      return o;
    };

    //HACK: this should be sync, but there is no easy way to convert a blob to base64 synchronously
    convertBlobs(this.vueFormData).then(val => {
      this.ignoreUpdateOnAttribute = true;
      this.attribute.value = !val ? '' : JSON.stringify(val);
      this.$nextTick(() => this.ignoreUpdateOnAttribute = false);
    });
  }
}
